import { ApiError } from "@/lib/apiClient";
import {
  Ticket,
  TicketMessage,
  TicketTag,
  CreateTicketDto,
  UpdateTicketDto,
  CreateTicketMessageDto,
  FetchTicketsOptions,
  SearchTicketsOptions,
} from "@/types/models/ticket";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface TicketsState {
  // Ticket list state
  items: Ticket[];
  loaded: boolean;
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  // Single ticket state
  currentTicket: Ticket | null;
  ticketLoading: boolean;
  ticketError: ApiError | null;

  // Search state
  searchResults: Ticket[];
  searchLoading: boolean;
  searchError: ApiError | null;
  searchPagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  // Messages state
  messages: TicketMessage[];
  messagesLoading: boolean;
  messagesError: ApiError | null;

  // Form states
  formState: {
    loading: boolean;
    success: boolean;
    error: ApiError | null;
  };

  messageFormState: {
    loading: boolean;
    success: boolean;
    error: ApiError | null;
  };

  // Tags state
  tags: TicketTag[];
  tagsLoading: boolean;
  tagsError: ApiError | null;

  // Stats state
  stats: {
    totalTickets: number;
    openTickets: number;
    resolvedTickets: number;
    avgResponseTime: number;
    loading: boolean;
    error: ApiError | null;
  };
}

const initialState: TicketsState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  pagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  currentTicket: null,
  ticketLoading: false,
  ticketError: null,
  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 0,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },
  messages: [],
  messagesLoading: false,
  messagesError: null,
  formState: {
    loading: false,
    success: false,
    error: null,
  },
  messageFormState: {
    loading: false,
    success: false,
    error: null,
  },
  tags: [],
  tagsLoading: false,
  tagsError: null,
  stats: {
    totalTickets: 0,
    openTickets: 0,
    resolvedTickets: 0,
    avgResponseTime: 0,
    loading: false,
    error: null,
  },
};

const ticketsSlice = createSlice({
  name: "tickets",
  initialState,
  reducers: {
    // Fetch Tickets
    fetchTicketsRequest: (
      state,
      _action: PayloadAction<FetchTicketsOptions>,
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchTicketsSuccess: (
      state,
      action: PayloadAction<{
        list: Ticket[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.items = action.payload.list;
      state.pagination.page = action.payload.page;
      state.pagination.total = action.payload.total;
      state.pagination.pageSize = action.payload.pageSize;
      state.pagination.totalPages = action.payload.totalPages;
      state.loading = false;
      state.loaded = true;
    },
    fetchTicketsFailure: (state, action: PayloadAction<ApiError>) => {
      state.error = action.payload;
      state.loading = false;
      state.loaded = true;
    },

    // Search Tickets
    searchTicketsRequest: (
      state,
      _action: PayloadAction<SearchTicketsOptions>,
    ) => {
      state.searchLoading = true;
      state.searchError = null;
    },
    searchTicketsSuccess: (
      state,
      action: PayloadAction<{
        list: Ticket[];
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      }>,
    ) => {
      state.searchResults = action.payload.list;
      state.searchPagination.page = action.payload.page;
      state.searchPagination.total = action.payload.total;
      state.searchPagination.pageSize = action.payload.pageSize;
      state.searchPagination.totalPages = action.payload.totalPages;
      state.searchLoading = false;
    },
    searchTicketsFailure: (state, action: PayloadAction<ApiError>) => {
      state.searchError = action.payload;
      state.searchLoading = false;
    },

    // Single Ticket Operations
    fetchTicketRequest: (
      state,
      _action: PayloadAction<{ ticketId: string }>,
    ) => {
      state.ticketLoading = true;
      state.ticketError = null;
    },
    fetchTicketSuccess: (state, action: PayloadAction<{ ticket: Ticket }>) => {
      state.currentTicket = action.payload.ticket;
      state.ticketLoading = false;
      state.ticketError = null;
    },
    fetchTicketFailure: (state, action: PayloadAction<ApiError>) => {
      state.ticketLoading = false;
      state.ticketError = action.payload;
    },

    // Create Ticket
    createTicketRequest: (state, _action: PayloadAction<CreateTicketDto>) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createTicketSuccess: (state, action: PayloadAction<{ ticket: Ticket }>) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items.unshift(action.payload.ticket);
      state.pagination.total += 1;
    },
    createTicketFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Update Ticket
    updateTicketRequest: (
      state,
      _action: PayloadAction<{ ticketId: string; ticket: UpdateTicketDto }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateTicketSuccess: (state, action: PayloadAction<{ ticket: Ticket }>) => {
      state.formState.loading = false;
      state.formState.success = true;
      const index = state.items.findIndex(
        (item) => item.id === action.payload.ticket.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload.ticket;
      }
      if (state.currentTicket?.id === action.payload.ticket.id) {
        state.currentTicket = action.payload.ticket;
      }
    },
    updateTicketFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
      state.formState.success = false;
    },

    // Messages
    fetchTicketMessagesRequest: (
      state,
      _action: PayloadAction<{
        ticketId: string;
        page?: number;
        limit?: number;
      }>,
    ) => {
      state.messagesLoading = true;
      state.messagesError = null;
    },
    fetchTicketMessagesSuccess: (
      state,
      action: PayloadAction<{ messages: TicketMessage[] }>,
    ) => {
      state.messages = action.payload.messages;
      state.messagesLoading = false;
    },
    fetchTicketMessagesFailure: (state, action: PayloadAction<ApiError>) => {
      state.messagesLoading = false;
      state.messagesError = action.payload;
    },

    createTicketMessageRequest: (
      state,
      _action: PayloadAction<CreateTicketMessageDto>,
    ) => {
      state.messageFormState.loading = true;
      state.messageFormState.error = null;
      state.messageFormState.success = false;
    },
    createTicketMessageSuccess: (
      state,
      action: PayloadAction<{ message: TicketMessage }>,
    ) => {
      state.messageFormState.loading = false;
      state.messageFormState.success = true;
      state.messages.push(action.payload.message);

      // Update current ticket's last message info
      if (state.currentTicket) {
        state.currentTicket.lastMessageAt = action.payload.message.created_at;
        state.currentTicket.messageCount += 1;
      }
    },
    createTicketMessageFailure: (state, action: PayloadAction<ApiError>) => {
      state.messageFormState.loading = false;
      state.messageFormState.error = action.payload;
      state.messageFormState.success = false;
    },

    // Tags
    fetchTicketTagsRequest: (state) => {
      state.tagsLoading = true;
      state.tagsError = null;
    },
    fetchTicketTagsSuccess: (
      state,
      action: PayloadAction<{ tags: TicketTag[] }>,
    ) => {
      state.tags = action.payload.tags;
      state.tagsLoading = false;
    },
    fetchTicketTagsFailure: (state, action: PayloadAction<ApiError>) => {
      state.tagsLoading = false;
      state.tagsError = action.payload;
    },

    // Stats
    fetchTicketStatsRequest: (
      state,
      _action: PayloadAction<{ period?: string }>,
    ) => {
      state.stats.loading = true;
      state.stats.error = null;
    },
    fetchTicketStatsSuccess: (
      state,
      action: PayloadAction<{
        totalTickets: number;
        openTickets: number;
        resolvedTickets: number;
        avgResponseTime: number;
      }>,
    ) => {
      state.stats = {
        ...action.payload,
        loading: false,
        error: null,
      };
    },
    fetchTicketStatsFailure: (state, action: PayloadAction<ApiError>) => {
      state.stats.loading = false;
      state.stats.error = action.payload;
    },

    // Reset states
    resetFormState: (state) => {
      state.formState.loading = false;
      state.formState.error = null;
      state.formState.success = false;
    },
    resetMessageFormState: (state) => {
      state.messageFormState.loading = false;
      state.messageFormState.error = null;
      state.messageFormState.success = false;
    },
    clearCurrentTicket: (state) => {
      state.currentTicket = null;
      state.ticketError = null;
      state.messages = [];
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchError = null;
    },

    // Real-time updates
    ticketUpdatedRealtime: (
      state,
      action: PayloadAction<{ ticket: Ticket }>,
    ) => {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.ticket.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload.ticket;
      }
      if (state.currentTicket?.id === action.payload.ticket.id) {
        state.currentTicket = action.payload.ticket;
      }
    },
    newMessageRealtime: (
      state,
      action: PayloadAction<{ message: TicketMessage }>,
    ) => {
      state.messages.push(action.payload.message);

      // Update current ticket's message info
      if (
        state.currentTicket &&
        state.currentTicket.id === action.payload.message.ticketId
      ) {
        state.currentTicket.lastMessageAt = action.payload.message.created_at;
        state.currentTicket.messageCount += 1;
        state.currentTicket.unreadCount += 1;
      }

      // Update ticket in list
      const ticketIndex = state.items.findIndex(
        (item) => item.id === action.payload.message.ticketId,
      );
      if (ticketIndex !== -1) {
        state.items[ticketIndex].lastMessageAt =
          action.payload.message.created_at;
        state.items[ticketIndex].messageCount += 1;
        state.items[ticketIndex].unreadCount += 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  // Fetch tickets
  fetchTicketsRequest,
  fetchTicketsSuccess,
  fetchTicketsFailure,

  // Search tickets
  searchTicketsRequest,
  searchTicketsSuccess,
  searchTicketsFailure,

  // Single ticket
  fetchTicketRequest,
  fetchTicketSuccess,
  fetchTicketFailure,

  // CRUD
  createTicketRequest,
  createTicketSuccess,
  createTicketFailure,
  updateTicketRequest,
  updateTicketSuccess,
  updateTicketFailure,

  // Messages
  fetchTicketMessagesRequest,
  fetchTicketMessagesSuccess,
  fetchTicketMessagesFailure,
  createTicketMessageRequest,
  createTicketMessageSuccess,
  createTicketMessageFailure,

  // Tags
  fetchTicketTagsRequest,
  fetchTicketTagsSuccess,
  fetchTicketTagsFailure,

  // Stats
  fetchTicketStatsRequest,
  fetchTicketStatsSuccess,
  fetchTicketStatsFailure,

  // Reset/Clear
  resetFormState,
  resetMessageFormState,
  clearCurrentTicket,
  clearSearchResults,

  // Real-time
  ticketUpdatedRealtime,
  newMessageRealtime,
} = ticketsSlice.actions;

export default ticketsSlice.reducer;
